import React, { useState } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Users, Sparkles, Briefcase, CalendarDays,
  CreditCard, AlertTriangle, MessageSquare, LogOut, Menu, X, Mail, BarChart3, Settings as SettingsIcon, Tag
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
  { to: "/admin/bookings", label: "Bookings", icon: Mail, testid: "nav-bookings" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/admin/cleaners", label: "Cleaners", icon: Sparkles, testid: "nav-cleaners" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
  { to: "/admin/revenue", label: "Revenue Share", icon: BarChart3, testid: "nav-revenue" },
  { to: "/admin/complaints", label: "Complaints", icon: AlertTriangle, testid: "nav-complaints" },
  { to: "/admin/templates", label: "Templates", icon: MessageSquare, testid: "nav-templates" },
  { to: "/admin/discount-leads", label: "Discount Leads", icon: Tag, testid: "nav-discount-leads" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-brand-appBg flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 top-0 left-0 h-full w-64 bg-white border-r border-slate-200 transform transition-transform flex flex-col ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-5 border-b border-slate-200 shrink-0">
          <Link to="/admin" data-testid="admin-logo-link"><Logo /></Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 min-h-0">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#1B3A6B] text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 shrink-0">
          <div className="px-3 py-2 text-xs text-slate-500">
            <div className="font-semibold text-slate-700 truncate">{user?.name}</div>
            <div className="truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-14 bg-white border-b border-slate-200 px-4 flex items-center justify-between">
          <button onClick={() => setOpen(!open)} className="p-2" aria-label="Toggle menu" data-testid="admin-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
          <Logo />
          <div className="w-9" />
        </header>
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
